import { PropsWithChildren, createContext } from 'react';

import { KnockProvider } from '@knocklabs/react';

import { useKnockToken } from '@/pages/NotificationSettings/hooks/useKnockToken';
import InAppNotificationFeedProvider from '@/providers/InAppNotificationFeedProvider';
import { useSession } from '@/providers/SessionProvider/SessionProvider';

const KNOCK_API_KEY = import.meta.env.VITE_KNOCK_PUBLIC_API_KEY ?? '';

export const InAppNotificationContext = createContext<{
  hasFeed: boolean;
} | null>(null);

const InAppNotificationProviderWrapper = ({
  children,
  hasFeed,
}: PropsWithChildren<{ hasFeed: boolean }>) => {
  return (
    <InAppNotificationContext.Provider value={{ hasFeed }}>
      {children}
    </InAppNotificationContext.Provider>
  );
};

const InAppNotificationProvider = ({ children }: PropsWithChildren) => {
  const { currentUser, currentClient } = useSession();
  const { id: accountId } = currentClient ?? {};

  const { knockToken, refreshKnockToken } = useKnockToken({
    enable: !!KNOCK_API_KEY,
  });

  const userId = currentUser?.id ?? '';

  /**
   * When in-app notifications are disabled, we don't initialize Knock.
   */
  if (!knockToken) {
    return (
      <InAppNotificationProviderWrapper hasFeed={false}>
        {children}
      </InAppNotificationProviderWrapper>
    );
  }

  /**
   * When running the app under E2E tests, no user token is provided to DEV instance.
   */
  if (import.meta.env.VITE_E2E) {
    return (
      <KnockProvider apiKey={KNOCK_API_KEY} userId={userId}>
        <InAppNotificationProviderWrapper hasFeed>
          <InAppNotificationFeedProvider>
            {children}
          </InAppNotificationFeedProvider>
        </InAppNotificationProviderWrapper>
      </KnockProvider>
    );
  }

  return (
    <KnockProvider
      apiKey={KNOCK_API_KEY}
      userId={userId}
      userToken={knockToken}
      onUserTokenExpiring={() => refreshKnockToken()}
    >
      <InAppNotificationProviderWrapper hasFeed>
        <InAppNotificationFeedProvider accountId={accountId}>
          {children}
        </InAppNotificationFeedProvider>
      </InAppNotificationProviderWrapper>
    </KnockProvider>
  );
};

export default InAppNotificationProvider;
